import { toast } from "react-toastify";

import {
  UpdateAgenticLogicRuleRequestBodyType,
  UpdateAgenticLogicRuleRequestPathParamsType,
  UpdateAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

const updateAgenticLogicRule = async (
  payload: ApiRequest<
    UpdateAgenticLogicRuleRequestBodyType,
    UpdateAgenticLogicRuleRequestPathParamsType
  >
) => {
  const resp = await api.patch<
    UpdateAgenticLogicRuleResponseBodyType,
    UpdateAgenticLogicRuleRequestBodyType
  >(`/v2/agentic-logic/rules/${payload.pathParams.ruleId}`, payload.body);
  return resp.data;
};

export const useUpdateAgenticLogicRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateAgenticLogicRuleResponseBodyType,
    Error,
    ApiRequest<
      UpdateAgenticLogicRuleRequestBodyType,
      UpdateAgenticLogicRuleRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => updateAgenticLogicRule(data),
    onSuccess: async (_data, payload) => {
      const { ruleId } = payload.pathParams;
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rules(),
      });
      // The draft graph is stamped from the rule's CURRENT settings on every
      // read, so a settings change has to refetch it or the canvas keeps
      // showing the old item count and empty-result policy.
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.draft(ruleId),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to update rule${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};

/**
 * Records which chat session authored a rule, once the rule exists — and, when
 * a revision session finishes, repoints the rule at that newer conversation so
 * a reload restores the transcript that explains the rule's current state.
 *
 * Separate from {@link useUpdateAgenticLogicRule} because this write is
 * background bookkeeping the author never asked for: it must not raise a
 * merchant-facing failure toast for something the merchant did not do. The
 * explicit onError below is what keeps it silent — without one, the
 * queryClient's default mutation onError toasts the raw server message.
 * Re-sending the stored id is a no-op server-side, so a lost call simply
 * costs that rule its latest transcript rather than corrupting anything.
 */
export const useLinkAgenticLogicRuleAuthoringSession = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateAgenticLogicRuleResponseBodyType,
    Error,
    { ruleId: string; authoringSessionId: string },
    unknown
  >({
    mutationFn: async ({ ruleId, authoringSessionId }) =>
      updateAgenticLogicRule({
        pathParams: { ruleId },
        body: { authoringSessionId },
        queryParams: undefined,
      }),
    onSuccess: async (_data, { ruleId }) => {
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
    },
    onError: () => {
      // Deliberately silent: see the hook comment.
    },
  });
};
